import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import policyService from '../../services/policyService';

const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

// 1. Fetch Vectors by Type (Human/Non-Human)
export const fetchVectorsByType = createAsyncThunk(
  'policies/fetchVectorsByType',
  async (type, { rejectWithValue }) => {
    try {
      const response = await policyService.getAttackVectorByType(type);
      // console.log("fetch-vectorbytype",response?.data?.attack_vectors)
      return { type, vectors: response?.data?.attack_vectors };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// 2. Fetch Use Cases for a specific Vector
export const fetchVectorUseCases = createAsyncThunk(
  'policies/fetchVectorUseCases',
  async (vectorId, { rejectWithValue }) => {
    try {
      const response = await policyService.getAttackVectorUsecases(vectorId);
      return { vectorId, useCases: response.data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// 3. Fetch Identities for a specific Use Case
export const fetchUseCaseIdentities = createAsyncThunk(
  'policies/fetchUseCaseIdentities',
  async ({ useCaseName, identityType, totalIdentities, page = 1, limit = 10,search }, { rejectWithValue }) => {
    try {
      // Pass page and limit to your service
      const response = await policyService.getUseCaseIdentities(
        useCaseName, 
        identityType, 
        page, 
        limit,
        search
      );

      return { 
        key: `${useCaseName}-${identityType}`, 
        identities: response?.data?.identities,
        totalCount: response?.data?.total, // Backend should return actual total
        page,
        isSearch: !!search
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchPolicyStats = createAsyncThunk(
  'policies/fetchPolicyStats',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { lastStatsUpdate } = getState().policies;
      const now = Date.now();

      // Check cache
      if (lastStatsUpdate && now - lastStatsUpdate < CACHE_DURATION) {
        return { cached: true };
      }

      const response = await policyService.getPolicyOverview();
      const data = response?.data || {};

      return {
        cached: false,
        data: data, 
      };
    } catch (error) {
      return rejectWithValue(error?.message || 'Failed to load policy stats');
    }
  }
);

export const IgnoreUseCaseforIdentity = createAsyncThunk(
  'policies/ignoreUseCaseIdentity',
  async ({ identitityId, riskId }, { rejectWithValue }) => {
    try {
      // Pass page and limit to your service
      const response = await policyService.backlogUseCaseIdentity(
        identitityId, 
        riskId, 
      );
      
      return response?.data
    } catch (error) {
      return rejectWithValue(error?.message);
    }
  }
);

const policiesSlice = createSlice({
  name: 'policies',
  initialState: {
    vectors: [],
    useCasesByVector: {}, // Map: { "AV1": [...] }
    useCasesLoadingStatus: {},
    identitiesByUseCase: {}, // Map: { "SSPR-human": [...] }
    summary: {
      total_attack_vectors: 0,
      human_attack_vectors: 0,
      non_human_attack_vectors: 0,
      flagged_usecases: 0
    },
    loading: false,
    statsLoading: false,
    modalLoading: false,
    activeFilterTab: 'human',
    selectedVectorId: 'all',
    templatePage: 1,
    policiesPerPage: 5,
    modalPagination: {
      currentPage: 1,
      itemsPerPage: 10,
      totalItems: 0
    }
  },
  reducers: {
    setActiveFilterTab: (state, action) => {
      state.activeFilterTab = action.payload;
      state.selectedVectorId = 'all';
      state.templatePage = 1;
    },
    setSelectedVectorId: (state, action) => {
      state.selectedVectorId = action.payload;
      state.templatePage = 1;
    },
    setTemplatePage: (state, action) => {
      state.templatePage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVectorsByType.pending, (state) => { state.loading = true; })
      .addCase(fetchVectorsByType.fulfilled, (state, action) => {
        state.loading = false;
        state.vectors = action.payload.vectors;
      })
      // .addCase(fetchVectorUseCases.fulfilled, (state, action) => {
      //   state.useCasesByVector[action.payload.vectorId] = action.payload.useCases;
      // })

      .addCase(fetchVectorUseCases.pending, (state, action) => {
        // action.meta.arg contains the vectorId passed into the thunk when dispatched
        const vectorId = action.meta.arg;
        state.useCasesLoadingStatus[vectorId] = 'loading';
      })
      .addCase(fetchVectorUseCases.fulfilled, (state, action) => {
        const { vectorId, useCases } = action.payload;
        state.useCasesLoadingStatus[vectorId] = 'succeeded';
        state.useCasesByVector[vectorId] = useCases;
      })
      .addCase(fetchVectorUseCases.rejected, (state, action) => {
        const vectorId = action.meta.arg;
        state.useCasesLoadingStatus[vectorId] = 'failed';
      })
      .addCase(fetchUseCaseIdentities.pending, (state) => { state.modalLoading = true; })
      .addCase(fetchUseCaseIdentities.fulfilled, (state, action) => {
          state.modalLoading = false;
          state.identitiesByUseCase[action.payload.key] = action.payload.identities;
          state.modalPagination.totalItems = action.payload.totalCount;
          state.modalPagination.currentPage = action.payload.page;
        })
      .addCase(fetchPolicyStats.pending, (state) => {
        state.statsLoading = true;
      })
      .addCase(fetchPolicyStats.fulfilled, (state, action) => {
        state.statsLoading = false;
        if (action.payload && !action.payload.cached) {
          state.summary = action.payload.data;
          state.lastStatsUpdate = Date.now();
        }
      })
      .addCase(fetchPolicyStats.rejected, (state) => {
        state.statsLoading = false;
      })
      .addCase(IgnoreUseCaseforIdentity.fulfilled, (state, action) => {
        // Update the count in Redux immediately
        if (state.modalPagination.totalItems > 0) {
          state.modalPagination.totalItems -= 1;
        }

        // Remove the ignored identity from the current list in Redux
        const ignoredId = action.meta.arg.identitityId;
        const cacheKey = Object.keys(state.identitiesByUseCase).find(key => 
          state.identitiesByUseCase[key].some(id => id.id === ignoredId)
        );

        if (cacheKey) {
          state.identitiesByUseCase[cacheKey] = state.identitiesByUseCase[cacheKey].filter(
            id => id.id !== ignoredId
          );
        }
      
    });
  },
});

export const { setActiveFilterTab, setSelectedVectorId, setTemplatePage,summary } = policiesSlice.actions;
export default policiesSlice.reducer;